import { paragraphDirection } from './textBidi';

// ── Text size in the print raster, and the letter cut it implies ────────────
// The editor stores fontSize as px on a 400-px-wide page (`fontSize/4 cqi`);
// the print rasterises at TEXT_RASTER_DPR px per point. Fonts with an optical-
// size axis (Fraunces) pick their letter shapes from the px size they are
// drawn at — in the DOM AND in a canvas. So the print got opsz ≈ 4.25 × size
// while a phone showed ~1.6 × size: same text, visibly different letters
// (Valencia check, 2026-10-13). The DOM pins the print's value instead.
export const TEXT_RASTER_DPR = 3;
export const EDITOR_REF_W = 400;
/** Interior trim width in points (200 mm — pdfGenerator PAGE_W). */
export const PRINT_PAGE_W_PT = 200 * 2.8346;

/** The space between lines, × the font size: the element's own (cover text,
 *  2026-10-30) or the editor's historical 1.3. The editor's `line-height` and
 *  the print's line pitch both read it here. */
export const DEFAULT_TEXT_LINE_HEIGHT = 1.3;
export function textLineHeight(el: { lineHeight?: unknown }): number {
  const v = Number(el.lineHeight);
  return Number.isFinite(v) && v >= 0.6 && v <= 4 ? v : DEFAULT_TEXT_LINE_HEIGHT;
}

/** The canvas px size the print draws a text element at. */
export function textRasterPx(fontSize: number | undefined, pageWPt = PRINT_PAGE_W_PT): number {
  return ((fontSize ?? 18) * pageWPt * TEXT_RASTER_DPR) / EDITOR_REF_W;
}

/** CSS `font-variation-settings` that gives the editor the print's letter cut.
 *  Fonts without an `opsz` axis ignore it. */
export function printOpticalSize(fontSize: number | undefined): string {
  return `'opsz' ${Math.round(textRasterPx(fontSize) * 10) / 10}`;
}

// Pure text-layout math for the PDF text-element raster (pdfGenerator's
// rasterizeTextElement). Extracted so the editor↔PDF overflow contract is
// unit-testable without a canvas.
//
// Editor contract this mirrors (TextOverlayChrome):
//   - the span wraps via CSS pre-wrap at the element box width;
//   - the box is a flex container with alignItems:center and the span has
//     overflow:visible → text TALLER than the stored el.height renders
//     anyway, vertically centred on the box centre, clipped only by the page.
// The 2026-06-12 intro bug ("Belgique/Amti Nezha" order): the raster clipped
// its canvas at the stored box height, so an 8-line dedication in a 12%-high
// box printed only the middle two lines while the editor showed all of it.

/** Word-wrap one text into lines at `wrapWidth`, using the caller's measure
 *  function (canvas measureText in prod, a fake in tests). Reproduces the
 *  editor's CSS semantics for the text spans, which use
 *  `white-space: pre-wrap` + `word-break: break-word` (TextOverlayChrome.tsx
 *  and both AlbumEditorPage text renders):
 *    - explicit \n respected (blank lines kept);
 *    - words that fit stay whole, wrapped at whitespace boundaries;
 *    - a SINGLE word wider than the box is broken CHARACTER-by-character into
 *      as many lines as needed so it never overflows the box (the CSS
 *      `break-word`/`overflow-wrap: anywhere` behaviour). Previously such a
 *      word was emitted on one over-wide line, which the raster then CLIPPED
 *      at the canvas edge — the "htdztdtrrstsgstrs" cut-text bug. */
export function wrapTextLines(
  text: string,
  wrapWidth: number,
  measure: (s: string) => number,
): string[] {
  // Break a single over-wide token (no whitespace) into char-fitted pieces.
  // Mirrors CSS `word-break: break-word`: greedily pack as many characters as
  // fit per line, breaking at any character boundary. Guarantees progress even
  // when a single glyph is wider than the box (emits that glyph alone).
  const breakLongWord = (word: string): string[] => {
    const pieces: string[] = [];
    let chunk = '';
    for (const ch of Array.from(word)) {
      const test = chunk + ch;
      if (chunk && measure(test) > wrapWidth) {
        pieces.push(chunk);
        chunk = ch;
      } else {
        chunk = test;
      }
    }
    if (chunk) pieces.push(chunk);
    return pieces.length ? pieces : [word];
  };

  // #1236 (2026-10-13): the customer typed RUNS OF SPACES to push words onto
  // the next line (« made under          the magical          lights of »).
  // The editor's `pre-wrap` keeps every space; this used to split on /\s+/,
  // collapsing each run to one space — the whole caption printed on ONE line.
  // Now the CSS rules, measured in Chrome on that album:
  //   · spaces are kept; a line may break only AFTER a run of spaces;
  //   · the spaces before a soft break stay on the line above and HANG — they
  //     never push a word down and never count for centring;
  //   · leading spaces of a paragraph are real content (they indent, and a run
  //     wider than the box fills a visually empty first line);
  //   · at the end of the paragraph the trailing spaces that still fit count.
  const wrapParagraph = (para: string): string[] => {
    if (para === '') return [''];
    const tokens = para.replace(/\t/g, ' ').match(/ +|[^ ]+/g) ?? [];
    const out: string[] = [];
    let cur = '';
    const softBreak = () => { out.push(cur.replace(/ +$/, '')); cur = ''; };
    for (const tok of tokens) {
      if (tok[0] === ' ') { cur += tok; continue; } // spaces never break a line
      const test = cur + tok;
      if (measure(test) <= wrapWidth) {
        cur = test;
      } else if (measure(tok) <= wrapWidth) {
        // Fits on its own line, not after what is here → wrap to a new line.
        if (cur !== '') softBreak();
        cur = tok;
      } else {
        // Wider than the box on its own → char-break it. Any text already on
        // the current line stays its own line first.
        if (cur !== '') softBreak();
        const pieces = breakLongWord(tok);
        // All but the last piece are full lines; the last continues `cur` so a
        // following short word can still pack onto it (matches CSS reflow).
        for (let i = 0; i < pieces.length - 1; i++) out.push(pieces[i]);
        cur = pieces[pieces.length - 1];
      }
    }
    // Paragraph end: trailing spaces count while they fit.
    while (/ $/.test(cur) && measure(cur) > wrapWidth) cur = cur.slice(0, -1);
    out.push(cur);
    return out;
  };
  return paragraphsOf(text).flatMap(wrapParagraph);
}

/**
 * The paragraphs the editor's pre-wrap span shows: split on \n, where ONE
 * trailing newline opens no line (#WA-269649 page 9 — the raster drew an
 * empty third line and centred the block half a line higher than the editor).
 */
function paragraphsOf(text: string): string[] {
  return text.replace(/\n$/, '').split('\n');
}

/**
 * Lines as the raster draws them, each with the direction of the paragraph it
 * belongs to — the first strong letter of the paragraph decides (Unicode
 * P2/P3), the same rule as the editor's `unicode-bidi: plaintext`. A wrapped
 * Arabic paragraph is right-to-left on every one of its lines.
 */
export function layoutTextLines(
  text: string,
  wrapWidth: number,
  measure: (s: string) => number,
): Array<{ text: string; dir: 'rtl' | 'ltr' }> {
  return paragraphsOf(text).flatMap((para) => {
    const dir = paragraphDirection(para);
    return wrapTextLines(para, wrapWidth, measure).map((line) => ({ text: line, dir }));
  });
}

/** Effective draw box for a text block: the stored box when the text fits,
 *  otherwise a box grown to the text height, centred on the stored box centre
 *  (= the editor's flex-center + overflow:visible). Coordinates are top-down
 *  (CSS-style); the caller converts to pdf-lib's bottom-up space. */
export function textBlockBox(
  boxTopPt: number,
  boxHeightPt: number,
  textHeightPt: number,
): { topPt: number; heightPt: number } {
  if (textHeightPt <= boxHeightPt) return { topPt: boxTopPt, heightPt: boxHeightPt };
  const centre = boxTopPt + boxHeightPt / 2;
  return { topPt: centre - textHeightPt / 2, heightPt: textHeightPt };
}

/**
 * Where the raster wraps: the element box, in canvas pixels — unrounded and
 * with NO inset. The editor's text box (TextOverlayChrome) has no padding; the
 * old « canvasW − 4 » wrapped a line that filled the last 0.8 % of a narrow box
 * in print but not on screen (seen 2026-10-06 on an Arabic caption).
 */
export function rasterWrapWidth(boxWidthPt: number, dpr: number): number {
  return Math.max(1, boxWidthPt * dpr);
}

// ─── COVER TEXT ZONES — the editor's rules, for the PDF (owner, 2026-10-17, #1248) ──
//
// The editor (CoverThumbnail `TextZone`) draws a zone as a flex box, text centred
// vertically, `line-height: 1.1`. The browser puts each baseline at
//   line-box top + half-leading + ascent,   half-leading = (1.1·fs − (ascent + descent)) / 2
// with the FONT'S OWN ascent/descent. The PDF had a hand-made baseline
// (`(lh − ascent)/2 + ascent − 0.2·fs` above the block's bottom) that, for
// Londrina Solid (ascent 0.945, descent 0.238), sits 0.63·fs HIGHER than the
// browser: « Afnane » printed into the top edge while the editor showed it clear.
// The editor also shrinks a zone's text to fit its box by width AND height (never
// below 30 %), only when nobody resized it by hand, and honours a font size set in
// the editor; the PDF only fitted the width and ignored that size.

export const COVER_LINE_HEIGHT = 1.1;

/** The font size a cover zone is drawn at — CoverThumbnail's `finalFontSize`. */
export function coverZoneFontSize(o: {
  /** The editor's font size (zone override) or the template's, at the 1860 px reference. */
  baseSize: number;
  /** panel width ÷ 1860. */
  fontScale: number;
  /** The customer's « scale » override (1 = none). */
  manualScale: number;
  zoneW: number;
  zoneH: number;
  lines: number;
  /** Widest line's width at a given size (font metrics; letter-spacing included). */
  widestAt: (size: number) => number;
}): number {
  const size = o.baseSize * o.fontScale * o.manualScale;
  let auto = 1;
  // The editor skips its auto-fit when the customer set a scale by hand.
  if (o.manualScale === 1 && size > 0) {
    const w = o.widestAt(size);
    const h = o.lines * size * COVER_LINE_HEIGHT;
    if (w > o.zoneW * 0.95 && o.zoneW > 0) auto = Math.min(auto, (o.zoneW * 0.95) / w);
    if (h > o.zoneH * 0.95 && o.zoneH > 0) auto = Math.min(auto, (o.zoneH * 0.95) / h);
    auto = Math.max(auto, 0.3);
  }
  return Math.max(6, size * auto);
}

/**
 * Each line's baseline, in PDF y-up points, for a zone whose box spans
 * [zoneBottom, zoneBottom + zoneH] — the browser's line-box maths.
 * `ascent` / `descent` are positive distances in points at `fontSize`.
 */
export function coverZoneBaselines(o: {
  zoneBottom: number;
  zoneH: number;
  lines: number;
  fontSize: number;
  ascent: number;
  descent: number;
}): number[] {
  const lh = o.fontSize * COVER_LINE_HEIGHT;
  const blockTop = o.zoneBottom + o.zoneH / 2 + (o.lines * lh) / 2;
  const halfLeading = (lh - (o.ascent + o.descent)) / 2;
  return Array.from({ length: o.lines }, (_, i) => blockTop - i * lh - halfLeading - o.ascent);
}
